/**
 * Signup page — Phase 2 of post-permissions / Public-SaaS rollout.
 *
 * Two paths in v1:
 *   1. Email + Password → POST /api/v1/auth/signup/email → JWT + tenant
 *      → user is created with email_verified=false, gets a magic link
 *      via comm-api to flip verified=true
 *   2. Google OAuth → /auth/google/authorize with mode=signup state →
 *      callback into POST /api/v1/auth/signup/google → JWT + tenant
 *
 * Facebook button is wired but disabled until auth-api's Facebook
 * provider lands (waiting on Alex' FB App credentials).
 *
 * Self-signup is server-gated by ALLOW_SELF_SIGNUP=true in auth-api's
 * .env. While off, both endpoints return 503 — we surface a friendly
 * "registration not enabled" message via SignupGatedError.
 */

import { useState } from 'react'
import { GOOGLE_LOGIN_ENABLED } from '../config'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  signupEmail,
  startGoogleSignup,
  SignupGatedError,
} from '../auth'

export function Signup() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const nextUrl = params.get('next') || '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ verifySent: boolean } | null>(null)

  const onSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      setError('Email und Passwort sind Pflicht.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const r = await signupEmail({
        email: email.trim(),
        password,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        next_url: nextUrl,
      })
      setSuccess({ verifySent: r.verification?.mail_sent ?? false })
      // Don't auto-navigate immediately — show "check your inbox" first.
      // After 3s, redirect anyway so the user lands in the app even if
      // they ignore the verify-mail (they just can't write until verified).
      window.setTimeout(() => navigate(nextUrl, { replace: true }), 3000)
    } catch (e) {
      if (e instanceof SignupGatedError) {
        setError('Public-Signup ist derzeit nicht aktiviert.')
      } else {
        setError((e as Error).message || String(e))
      }
    } finally {
      setBusy(false)
    }
  }

  const onGoogle = () => {
    setError(null)
    startGoogleSignup(nextUrl)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          padding: 28,
          borderRadius: 12,
          background: 'var(--surface, #fff)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: 24, marginBottom: 4 }}>
          Konto erstellen
        </h1>
        <p style={{ color: 'var(--text-muted, #666)', marginTop: 0, fontSize: 14 }}>
          Du bekommst dabei automatisch deinen eigenen Workspace.
        </p>

        {success && (
          <div
            style={{
              padding: 12,
              background: '#d4edda',
              color: '#155724',
              borderRadius: 6,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            <strong>Konto angelegt.</strong>
            {success.verifySent
              ? ' Bitte schau in dein Postfach und bestätige deine Email-Adresse.'
              : ' Die Verifizierungs-Mail konnte nicht versendet werden — bitte später erneut anfordern.'}
            <br />
            <span style={{ fontSize: 12, color: '#155724' }}>
              Du wirst in ein paar Sekunden weitergeleitet.
            </span>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: 10,
              background: '#fee',
              color: '#900',
              borderRadius: 6,
              marginBottom: 14,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {/* Provider Buttons — Google only when this build carries an
            OAuth client id (tenant-safe: never the operator's client) */}
        {GOOGLE_LOGIN_ENABLED && (
        <button
          onClick={onGoogle}
          disabled={busy || !!success}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid var(--border, #ccc)',
            background: 'white',
            cursor: busy || success ? 'not-allowed' : 'pointer',
            fontSize: 15,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 10,
            opacity: busy || success ? 0.5 : 1,
          }}
        >
          <span style={{ fontSize: 18 }}>🇬</span>
          Mit Google registrieren
        </button>
        )}

        <button
          disabled
          title="Facebook-Login folgt sobald die App-Credentials da sind"
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid var(--border, #ccc)',
            background: 'white',
            cursor: 'not-allowed',
            fontSize: 15,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 18,
            opacity: 0.5,
          }}
        >
          <span style={{ fontSize: 18 }}>📘</span>
          Mit Facebook registrieren (bald)
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            margin: '16px 0',
            color: 'var(--text-muted, #888)',
            fontSize: 12,
          }}
        >
          <span style={{ flex: 1, height: 1, background: 'var(--border, #e5e4e7)' }} />
          <span style={{ padding: '0 10px' }}>oder mit Email</span>
          <span style={{ flex: 1, height: 1, background: 'var(--border, #e5e4e7)' }} />
        </div>

        <form onSubmit={onSubmitEmail}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              placeholder="Vorname"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={busy || !!success}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Nachname"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={busy || !!success}
              style={inputStyle}
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy || !!success}
            style={{ ...inputStyle, width: '100%', marginBottom: 10 }}
          />
          <input
            type="password"
            placeholder="Passwort (mind. 8 Zeichen)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={busy || !!success}
            style={{ ...inputStyle, width: '100%', marginBottom: 14 }}
          />
          <button
            type="submit"
            disabled={busy || !!success}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 8,
              border: 0,
              background: 'var(--accent, #4a6cf7)',
              color: 'white',
              cursor: busy || success ? 'not-allowed' : 'pointer',
              fontSize: 15,
              fontWeight: 600,
              opacity: busy || success ? 0.5 : 1,
            }}
          >
            {busy ? 'Lade …' : 'Konto erstellen'}
          </button>
        </form>

        <div
          style={{
            marginTop: 18,
            fontSize: 13,
            textAlign: 'center',
            color: 'var(--text-muted, #666)',
          }}
        >
          Schon einen Account? <Link to="/login">Anmelden</Link>
        </div>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid var(--border, #ccc)',
  fontSize: 14,
  flex: 1,
  minWidth: 0,
}
